/**
 * spin-scrub.js — pointer-driven 360° turntable for every `[data-spin]` stage.
 *
 * A stage carries a poster <img>, a <canvas>, and data attributes:
 *   data-spin-count       number of frames (frame 0 is the front pose)
 *   data-spin-frames      URL template with `{i}` for the zero-padded index
 *   data-spin-frames-hd   optional sharper set, used when the stage is wider than HD_ABOVE_PX device pixels
 *   data-spin-turn-width  pointer travel, as a fraction of stage width, per full turn
 *
 * Frames are decoded <img> elements drawn on the canvas, never a seeking
 * <video>: a pointer move must not wait on a decode. Loading is progressive
 * (every 8th frame, then 4th, 2nd, then the rest) so the first drag works
 * within a few hundred milliseconds and gets smoother as frames arrive.
 *
 * Hover rotates on mouse; touch needs a sideways drag so the page keeps
 * scrolling vertically (touch-action: pan-y in CSS, horizontal intent here).
 * After the pointer leaves, the subject eases home along the shortest path.
 */

(function () {
  'use strict';

  var RETURN_DELAY_MS = 900;
  var FOLLOW = 0.18;
  var TOUCH_INTENT_PX = 6;
  // Phones keep the lighter set; desktop stages stretch it and it looks soft.
  var HD_ABOVE_PX = 1100;
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function pad3(i) {
    return i < 10 ? '00' + i : i < 100 ? '0' + i : String(i);
  }

  function initStage(el) {
    if (el.__spin) return;
    var count = parseInt(el.getAttribute('data-spin-count'), 10);
    var template = el.getAttribute('data-spin-frames') || '';
    var hd = el.getAttribute('data-spin-frames-hd');
    if (hd && (el.getBoundingClientRect().width || 0) * (window.devicePixelRatio || 1) > HD_ABOVE_PX) template = hd;
    var turnWidth = parseFloat(el.getAttribute('data-spin-turn-width')) || 1.1;
    var canvas = el.querySelector('canvas');
    if (!count || !template || !canvas) return;
    el.__spin = true;

    var ctx = canvas.getContext('2d');
    var images = new Array(count);
    var loaded = [];
    var loadedCount = 0;
    var failed = 0;
    var angle = 0;
    var target = 0;
    var drawn = -1;
    var raf = null;
    var returnTimer = null;
    var pointer = { x: 0, y: 0, active: false, dragging: false, decided: false };
    var interacted = false;

    function setStatus(s) {
      el.setAttribute('data-status', s);
      if (s === 'ready') el.setAttribute('tabindex', '0');
    }

    function nearestLoaded(i) {
      if (loaded[i]) return i;
      for (var d = 1; d < count; d += 1) {
        var a = (i + d) % count;
        var b = (i - d + count) % count;
        if (loaded[b]) return b;
        if (loaded[a]) return a;
      }
      return -1;
    }

    function shownAngle() {
      return ((Math.round(angle) % 360) + 360) % 360;
    }

    function draw() {
      var idx = ((Math.round((angle / 360) * count) % count) + count) % count;
      var use = nearestLoaded(idx);
      if (use < 0 || use === drawn) return;
      var img = images[use];
      if (!img) return;
      if (canvas.width !== img.naturalWidth || canvas.height !== img.naturalHeight) {
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
      }
      ctx.drawImage(img, 0, 0);
      drawn = use;
      el.setAttribute('aria-valuenow', String(shownAngle()));
      el.setAttribute('aria-valuetext', shownAngle() + ' degrees');
    }

    function tick() {
      raf = null;
      var diff = target - angle;
      if (reduceMotion || Math.abs(diff) < 0.05) angle = target;
      else angle += diff * FOLLOW;
      draw();
      if (angle !== target) raf = requestAnimationFrame(tick);
    }

    function schedule() {
      if (raf === null) raf = requestAnimationFrame(tick);
    }

    function markInteracted() {
      if (interacted) return;
      interacted = true;
      el.setAttribute('data-interacted', 'true');
    }

    function cancelReturn() {
      if (returnTimer !== null) {
        clearTimeout(returnTimer);
        returnTimer = null;
      }
    }

    function release() {
      pointer.active = false;
      pointer.dragging = false;
      pointer.decided = false;
      cancelReturn();
      returnTimer = setTimeout(function () {
        // Ease back to the front pose along the shortest path; a near-complete turn finishes forward.
        target = Math.round(target / 360) * 360;
        schedule();
      }, RETURN_DELAY_MS);
    }

    function rotateBy(dx) {
      var width = el.getBoundingClientRect().width || 1;
      target += (dx / (width * turnWidth)) * 360;
      markInteracted();
      schedule();
    }

    el.addEventListener('pointerenter', function (e) {
      if (e.pointerType !== 'mouse') return;
      cancelReturn();
      pointer = { x: e.clientX, y: e.clientY, active: true, dragging: false, decided: true };
    });

    el.addEventListener('pointerdown', function (e) {
      cancelReturn();
      var mouse = e.pointerType === 'mouse';
      // Touch: do not claim the gesture yet. A vertical swipe must stay a page scroll.
      pointer = { x: e.clientX, y: e.clientY, active: true, dragging: true, decided: mouse };
      if (mouse) el.setPointerCapture(e.pointerId);
    });

    el.addEventListener('pointermove', function (e) {
      var hover = e.pointerType === 'mouse';
      if (!pointer.active) {
        if (hover) pointer = { x: e.clientX, y: e.clientY, active: true, dragging: false, decided: true };
        return;
      }
      if (!hover && !pointer.dragging) return;
      if (!pointer.decided) {
        var ax = Math.abs(e.clientX - pointer.x);
        var ay = Math.abs(e.clientY - pointer.y);
        if (ax < TOUCH_INTENT_PX && ay < TOUCH_INTENT_PX) return;
        if (ay > ax) {
          // Vertical intent: hand the gesture back to the scroller.
          pointer.active = false;
          pointer.dragging = false;
          return;
        }
        pointer.decided = true;
        el.setPointerCapture(e.pointerId);
      }
      var dx = e.clientX - pointer.x;
      pointer.x = e.clientX;
      pointer.y = e.clientY;
      if (dx !== 0) rotateBy(dx);
    });

    function onUp(e) {
      if (el.hasPointerCapture && el.hasPointerCapture(e.pointerId)) el.releasePointerCapture(e.pointerId);
      pointer.dragging = false;
      if (e.pointerType !== 'mouse') release();
    }
    el.addEventListener('pointerup', onUp);
    el.addEventListener('pointercancel', onUp);
    el.addEventListener('pointerleave', function (e) {
      if (e.pointerType === 'mouse') release();
    });

    el.addEventListener('keydown', function (e) {
      var step = e.shiftKey ? 45 : 15;
      if (e.key === 'ArrowRight') target += step;
      else if (e.key === 'ArrowLeft') target -= step;
      else if (e.key === 'Home') target = Math.round(target / 360) * 360;
      else return;
      e.preventDefault();
      cancelReturn();
      markInteracted();
      schedule();
    });

    // Progressive preload: coarse ring first, then fill in.
    var order = [];
    var seen = {};
    [8, 4, 2, 1].forEach(function (step) {
      for (var i = 0; i < count; i += step) {
        if (!seen[i]) {
          seen[i] = true;
          order.push(i);
        }
      }
    });
    var inFlight = 0;
    var next = 0;
    setStatus('loading');

    function pump() {
      while (inFlight < 6 && next < order.length) {
        var i = order[next++];
        inFlight += 1;
        var img = new Image();
        img.decoding = 'async';
        img.onload = (function (index, image) {
          return function () {
            inFlight -= 1;
            images[index] = image;
            loaded[index] = true;
            loadedCount += 1;
            el.style.setProperty('--spin-progress', String(loadedCount / count));
            if (loadedCount === 1) setStatus('ready');
            if (index === 0 || loadedCount % 8 === 0 || loadedCount === count) draw();
            if (loadedCount === count) el.setAttribute('data-complete', 'true');
            pump();
          };
        })(i, img);
        img.onerror = function () {
          inFlight -= 1;
          failed += 1;
          if (failed > count / 4 && loadedCount === 0) setStatus('unavailable');
          pump();
        };
        img.src = template.replace('{i}', pad3(i));
      }
    }
    pump();
  }

  // A gallery renders a stage per layout and hides the one not in use; a hidden stage waits until it has a
  // size, so it never downloads frames nobody sees and picks its frame set for the size it is shown at.
  var sizeWatch = window.ResizeObserver
    ? new ResizeObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.contentRect.width > 0) {
            sizeWatch.unobserve(entry.target);
            initStage(entry.target);
          }
        });
      })
    : null;

  function initAll(root) {
    var stages = (root || document).querySelectorAll('[data-spin]');
    for (var i = 0; i < stages.length; i += 1) {
      if (sizeWatch && !stages[i].getBoundingClientRect().width) sizeWatch.observe(stages[i]);
      else initStage(stages[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }
  // Theme editor re-renders sections without a reload.
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });
})();
